import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import _ from 'lodash';
import { validateInternalId } from 'utils/internalId.validation';
import { validateObjectId } from 'utils/objectId.validation';
import { assertWithId } from 'utils/sliceHelpers/utils';

export type IdType = 'integer' | 'objectId';

export const ID_VALIDATORS = {
  integer: validateInternalId,
  objectId: (value: string) => !!validateObjectId(value),
} as const satisfies Record<IdType, (value: string) => boolean>;

const useSelectResource = <T extends object = Record<string, unknown>>(
  idType: IdType,
  resourceMap: Record<string, T>,
  resource1: T,
  resource2: T,
  setResource1: (resource: T) => void,
  setResource2: (resource: T) => void,
  newValue: string = 'new'
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isEntry = useRef(true);
  const [resource1Id, setResource1Id] = useState<string | undefined>();
  const [resource2Id, setResource2Id] = useState<string | undefined>();

  useEffect(() => {
    if (searchParams && resourceMap && isEntry.current) {
      const id1 = getValidatedValue(
        searchParams.get('item1'),
        idType,
        newValue
      );
      const id2 = getValidatedValue(
        searchParams.get('item2'),
        idType,
        newValue
      );
      setResource1Id(id1);
      setResource2Id(id2);
      if (id1 && resourceMap[id1]) {
        setResource1(resourceMap[id1]);
      }
      if (id2 && resourceMap[id2]) {
        setResource2(resourceMap[id2]);
      }
      if (!_.isEmpty(resourceMap)) {
        isEntry.current = false;
      }
    }
  }, [
    searchParams,
    resourceMap,
    isEntry,
    setResource1,
    setResource2,
    idType,
    newValue,
  ]);

  useEffect(() => {
    // If it's the first time we enter the page, we don't modify the search params
    if (isEntry.current) return;

    setSearchParams((oldParams) => {
      const newSearchParams = new URLSearchParams(oldParams);
      updateItemSearchParam(
        newSearchParams,
        'item1',
        resource1,
        idType,
        newValue
      );
      updateItemSearchParam(
        newSearchParams,
        'item2',
        resource2,
        idType,
        newValue
      );

      if (newSearchParams.toString() !== oldParams.toString()) {
        return newSearchParams;
      }
      return oldParams;
    });
  }, [isEntry, resource1, resource2, setSearchParams, idType, newValue]);

  return useMemo(
    () => _.compact([resource1Id, resource2Id]),
    [resource1Id, resource2Id]
  );
};

const updateItemSearchParam = (
  searchParams: URLSearchParams,
  key: string,
  object: object | undefined,
  idType: IdType,
  newValue: string
) => {
  const id = assertWithId(object) ? object.id : null;
  const valueStr = getValidatedValue(id?.toString(), idType, newValue);

  if (valueStr == null && searchParams.has(key)) {
    searchParams.delete(key);
  } else if (valueStr != null && searchParams.get(key) !== valueStr) {
    searchParams.set(key, valueStr);
  }
};

const getValidatedValue = (
  value: string | null | undefined,
  idType: IdType,
  newValue: string
): string | undefined => {
  if (value === newValue) return value;
  if (value == null || value === '') return undefined;
  if (!ID_VALIDATORS[idType](value)) return undefined;
  return value;
};

export default useSelectResource;
